import React, { useEffect, useMemo, useState } from "react"
import styles from "./AgentTypeForm.module.scss"
import { useTranslation } from "react-i18next"
import { AgentType } from "../../models/agent"
import SearchInput from "../SearchInput/SearchInput"
import FormAccordion from "../FormAccordion/FormAccordion"
import { TFunction } from "i18next"
import AgentTypeFormCard from "./AgentTypeFormCard"
import { useLazySearchApplicationTemplatesQuery } from "../../api/controllers/workshop"
import { useAppSelector } from "../../store/hooks"
import { selectApplicationTemplates } from "../../store/workshop/selectors"
import { ApplicationTemplate } from "../../models/workshop/template"
import { ApplicationType, TemplateVersionStatus } from "../../models/workshop/common"
import useRefCheck from "../../hooks/useRefCheck"
import { APPSTORE_ENABLED } from "../../utility/common/flags"

const tNamespace = "agent:"

const agentTypes = [
    AgentType.AIAssist2,
    AgentType.NightAgent,
    AgentType.RoutingAgent,
    AgentType.ABAgent,
    AgentType.RestAgent,
    AgentType.EmailAgent,
    AgentType.InitialMsgAgent,
    AgentType.RemoteWorkplaceAgent,
    AgentType.FixedReplyAgent,
    AgentType.FinishDialogAgent,
    AgentType.SurveysAgent,
    AgentType.IdentificationAgent,
    AgentType.JqAgent,
    AgentType.JSAgent,
    AgentType.ScoreAgent
]

const agentIntegrationTypes = [
    AgentType.MEDODSIntegration,
    AgentType.ADFSAgent,
    AgentType.PsbBssAgent,
    AgentType.YandexMapAgent,
    AgentType.YandexMapGeocodeAgent
]

interface AgentTypesWithTitle {
    type: AgentType
    title: string
}

interface AppstoreAgent extends AgentTypesWithTitle {
    template: ApplicationTemplate
}

interface Props {
    onSelect: (type: AgentType, template?: ApplicationTemplate) => void
    withIntegrations?: boolean
}

const filterTypes = (types: AgentTypesWithTitle[], query: string) => {
    const q = query.trim().toLowerCase()
    return q ? types.filter(t => t.title.toLowerCase().includes(q) || t.type.toLowerCase().includes(q)) : types
}

const filterAppstore = (agents: AppstoreAgent[], query: string) => {
    const cleanQuery = query.trim().toLowerCase()
    return cleanQuery
        ? agents.filter(
              agent => agent.title.toLowerCase().includes(cleanQuery) || agent.type.toLowerCase().includes(cleanQuery)
          )
        : agents
}

const addTitles =
    (t: TFunction) =>
    (type: AgentType): AgentTypesWithTitle => ({
        type,
        title: t(`${tNamespace}agent-type.${type}`)
    })

const AgentTypeForm: React.FC<Props> = props => {
    const { t } = useTranslation()
    const { onSelect, withIntegrations = true } = props
    const [searchInput, setSearchInput] = useState("")
    const applicationTemplates = useAppSelector(selectApplicationTemplates)

    const isAppstoreEnabled = useRefCheck(APPSTORE_ENABLED)

    const [getApplicationTemplates] = useLazySearchApplicationTemplatesQuery()

    const agentTypesWithTitle: AgentTypesWithTitle[] = useMemo(() => agentTypes.map(addTitles(t)), [t])

    const agentIntegrationTypesWithTitle: AgentTypesWithTitle[] = useMemo(
        () => agentIntegrationTypes.map(addTitles(t)),
        [t]
    )

    const agentAppstoreTypes: AppstoreAgent[] = useMemo(
        () =>
            [...applicationTemplates]
                .sort((a, b) => a.Audit.UpdateTimestamp - b.Audit.UpdateTimestamp)
                .map(template => ({ type: AgentType.AppstoreAgent, title: template.Title, template })),
        [applicationTemplates]
    )

    useEffect(() => {
        isAppstoreEnabled &&
            getApplicationTemplates({
                IsDeleted: false,
                Status: TemplateVersionStatus.PUBLISH,
                ApplicationType: ApplicationType.AGENT
            })
    }, [isAppstoreEnabled])

    return (
        <div className={styles.agentTypeForm}>
            <SearchInput
                onChange={searchValue => setSearchInput(searchValue)}
                className={styles.agentTypeForm__searchInput}
            />
            <div className={styles.agentTypeForm__content}>
                <FormAccordion
                    title={t(`${tNamespace}basic`)}
                    initialOpened
                    className={styles.agentTypeForm__accordion}
                >
                    {filterTypes(agentTypesWithTitle, searchInput).map(v => (
                        <AgentTypeFormCard key={v.type} type={v.type} title={v.title} onSelect={onSelect} t={t} />
                    ))}
                </FormAccordion>
                {withIntegrations && (
                    <FormAccordion
                        title={t(`${tNamespace}integrations`)}
                        initialOpened
                        className={styles.agentTypeForm__accordion}
                    >
                        {filterTypes(agentIntegrationTypesWithTitle, searchInput).map(v => (
                            <AgentTypeFormCard key={v.type} type={v.type} title={v.title} onSelect={onSelect} t={t} />
                        ))}
                    </FormAccordion>
                )}
                {isAppstoreEnabled && (
                    <FormAccordion
                        title={t(`${tNamespace}appstore`)}
                        initialOpened
                        className={styles.agentTypeForm__accordion}
                    >
                        {filterAppstore(agentAppstoreTypes, searchInput).map(v => (
                            <AgentTypeFormCard
                                key={v.template.Id}
                                type={v.type}
                                title={v.title}
                                logo={v.template.Icon}
                                onSelect={type => {
                                    onSelect(type, v.template)
                                }}
                                description={v.template.Description}
                                t={t}
                            />
                        ))}
                    </FormAccordion>
                )}
            </div>
        </div>
    )
}

export default AgentTypeForm
